import { alpha, createTheme, type ThemeOptions } from "@mui/material/styles";

/**
 * CloudForge ERM design system (CF-0025).
 * Dense, high-contrast, industrial. Tokens here are the single source of truth: components,
 * status colors and spacing derive from them. Dark mode uses the same tokens with inverted surfaces.
 */
export const tokens = {
  brand: { primary: "#1F4E79", primaryDark: "#173A5A", accent: "#E07A1F" },
  surface: { canvas: "#F4F6F8", paper: "#FFFFFF", sunken: "#EEF1F4", border: "#D9DEE3" },
  text: { primary: "#1B2430", secondary: "#5A6673", disabled: "#98A2AD" },
  status: {
    draft: "#6B7785",
    pending: "#B26A00",
    active: "#2E7D32",
    hold: "#C62828",
    done: "#1F4E79",
    cancelled: "#8E8E8E",
  },
  radius: 6,
  density: { rowHeight: 36, inputHeight: 32 },
} as const;

export type StatusTone = keyof typeof tokens.status;

const base: ThemeOptions = {
  shape: { borderRadius: tokens.radius },
  spacing: 8,
  typography: {
    fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
    fontSize: 13,
    h1: { fontSize: "2rem", fontWeight: 600 },
    h2: { fontSize: "1.6rem", fontWeight: 600 },
    h3: { fontSize: "1.4rem", fontWeight: 600 },
    h4: { fontSize: "1.25rem", fontWeight: 600 },
    h5: { fontSize: "1.1rem", fontWeight: 600 },
    h6: { fontSize: "0.95rem", fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    body2: { fontSize: "0.8125rem" },
    caption: { fontSize: "0.72rem" },
    button: { textTransform: "none", fontWeight: 600 },
  },
  components: {
    MuiButton: { defaultProps: { size: "small", disableElevation: true } },
    MuiIconButton: { defaultProps: { size: "small" } },
    MuiTextField: { defaultProps: { size: "small", variant: "outlined", fullWidth: true } },
    MuiSelect: { defaultProps: { size: "small" } },
    MuiAutocomplete: { defaultProps: { size: "small" } },
    MuiChip: { defaultProps: { size: "small" } },
    MuiTableCell: { styleOverrides: { root: { padding: "6px 12px" } } },
    MuiCard: { defaultProps: { variant: "outlined" } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
    MuiTooltip: { defaultProps: { arrow: true, enterDelay: 400 } },
    MuiDialog: { defaultProps: { maxWidth: "sm", fullWidth: true } },
    MuiListItemButton: { styleOverrides: { root: { borderRadius: tokens.radius } } },
  },
};

export function buildTheme(mode: "light" | "dark" = "light") {
  const dark = mode === "dark";
  return createTheme({
    ...base,
    palette: {
      mode,
      primary: { main: dark ? "#6FA3D6" : tokens.brand.primary, dark: tokens.brand.primaryDark },
      secondary: { main: tokens.brand.accent },
      background: dark
        ? { default: "#0F1720", paper: "#16202B" }
        : { default: tokens.surface.canvas, paper: tokens.surface.paper },
      text: dark
        ? { primary: "#E6EBF0", secondary: "#A6B2BE", disabled: "#6C7883" }
        : { primary: tokens.text.primary, secondary: tokens.text.secondary, disabled: tokens.text.disabled },
      divider: dark ? alpha("#FFFFFF", 0.12) : tokens.surface.border,
      success: { main: tokens.status.active },
      warning: { main: tokens.status.pending },
      error: { main: tokens.status.hold },
      info: { main: tokens.status.done },
    },
  });
}

export const theme = buildTheme("light");
